import { Button, Col, Form, Row, OverlayTrigger, Tooltip } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { ChangeEvent, FormEvent } from "react"
import Select from "react-select"
import { useAuth } from "../auth/useAuth"
import { FormMode } from "../../types/common"

type UserFormProps = {
    state: any
    usertype: string
    sucursals: any[]
    handleInputChange: (e: any) => void
    submit: (e: FormEvent<HTMLFormElement>) => void
    isAddingOrEditing: FormMode
    isRegistering: boolean
}

export default function UserForm({ state, usertype, sucursals, handleInputChange, submit, isAddingOrEditing, isRegistering }: UserFormProps) {
    const { t } = useTranslation()
    const { clientauth } = useAuth()

    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target
        handleInputChange({ target: { name, value: checked } })
    }

    return (
        <Form onSubmit={submit} className="px-4 formulario">
            <Row className="my-3">
                <Col xs={12} md={6}>
                    <Form.Label>{t('userTranslate.name')}</Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='user'
                        value={state.user || ''}
                        onChange={handleInputChange}
                    />
                </Col>
                <Col xs={12} md={6}>
                    <Form.Label>{t('userTranslate.phone')}</Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='phone'
                        value={state.phone || ''}
                        onChange={handleInputChange}
                    />
                </Col>
            </Row>
            <Row className="my-3">
                <Col xs={12} md={6}>
                    <Form.Label>{t('userTranslate.email')} ({t('global.mandatory')})</Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='email'
                        value={state.email || ''}
                        onChange={handleInputChange}
                        required
                    />
                </Col>
                <Col xs={12} md={6}>
                    <Form.Label>
                        {t('userTranslate.password')}{isAddingOrEditing !== "editing" ? ' (' + t('global.mandatory') + ')' : ''} &nbsp;
                        {isAddingOrEditing === 'editing' &&
                            <OverlayTrigger
                                placement="right"
                                delay={{ show: 250, hide: 400 }}
                                overlay={
                                    <Tooltip>
                                        {t('userTranslate.passwordTooltip')}
                                    </Tooltip>
                                }
                            >
                                <i className="fas fa-info-circle"></i>
                            </OverlayTrigger>
                        }
                    </Form.Label>
                    <Form.Control
                        className="sd-input"
                        type='text'
                        name='password'
                        value={state.password || ''}
                        onChange={handleInputChange}
                        required={isAddingOrEditing !== "editing" ? true : false}
                    />
                </Col>
            </Row>
            {usertype === 'admin' || (isAddingOrEditing === 'editing' && clientauth?.id !== state?._id) ?
                <>
                    <Row className="my-3">
                        {usertype === 'admin' ?
                            <Col xs={12} md={6}>
                                <Form.Group className='form-group'>
                                    <Form.Label>{t('userTranslate.rol')} ({t('global.mandatory')})</Form.Label>
                                    <Select
                                        required
                                        className="form-control select"
                                        name='rol'
                                        options={['clerk', 'commercial', 'admin'].map(role => ({ label: t(role), value: role }))}
                                        defaultValue={{ label: t('clerk'), value: 'clerk' }}
                                        value={state.rol ? { label: t(state.rol), value: state.rol } : null}
                                        onChange={(selectedValue) => handleInputChange({ target: { name: 'rol', value: (selectedValue as any).value } })}
                                        placeholder={t('global.select')}
                                        isSearchable={false}
                                    />
                                </Form.Group>
                            </Col>
                            :
                            <></>
                        }
                        <Col xs={12} md={6}>
                            <Form.Label>{t('userTranslate.ente')}</Form.Label>
                            <Form.Control
                                className="sd-input"
                                type='text'
                                name='idEnte'
                                value={state.idEnte || ''}
                                onChange={handleInputChange}
                            />
                        </Col>
                    </Row>
                    <Row className="my-3">
                        <Col xs={12} md={6}>
                            <Form.Group className='form-group'>
                                <Form.Label>{t('userTranslate.sucursal')}</Form.Label>
                                <Select
                                    className="form-control select"
                                    name='idSucursal'
                                    options={sucursals?.map(sucursal => ({
                                        label: sucursal.descripcion,
                                        value: sucursal.ebrokerId
                                    })) ?? []}
                                    value={(state.idSucursal !== undefined && state.idSucursal !== null) ? {
                                        label: sucursals?.find(sucursal => Number(sucursal.ebrokerId) === Number(state.idSucursal))?.descripcion ?? String(state.idSucursal),
                                        value: state.idSucursal
                                    } : null}
                                    onChange={(selectedValue) => handleInputChange({ target: { name: 'idSucursal', value: (selectedValue as any)?.value ?? null } })}
                                    placeholder={t('global.select')}
                                    isSearchable
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </>
                :
                <></>
            }
            {state?.rol === 'clerk' ?
                <Row>
                    <Col xs={12} md={6}>
                        <Form.Group className='form-group'>
                            <Form.Check
                                type="checkbox"
                                label={t('userTranslate.isAdminClerk')}
                                name="isAdminClerk"
                                checked={state.isAdminClerk}
                                onChange={handleSwitchChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                :
                <></>
            }
            <Row>
                <Col xs={12} lg={6} className="my-2 ms-auto">
                    <Button variant='primary' type="submit" className="w-100 my-2">
                        {isRegistering ?
                            <>
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </>
                            :
                            isAddingOrEditing === 'editing' ?
                                <>{t('global.edit')}</>
                                :
                                <>{t('global.register')}</>
                        }
                    </Button>
                </Col>
            </Row>
        </Form>
    )
}